import {DOCUMENT} from '@angular/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {environment} from 'src/environments/environment';

import {Logger} from '../../@core';
import {Credentials, CredentialsService} from './credentials.service';


// tslint:disable-next-line:variable-name
const _log: Logger = new Logger();

export interface LoginContext {
    username: string;
    password: string;
    remember?: boolean;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    constructor(
        @Inject(DOCUMENT) private document: Document,
        private router: Router,
        private httpClient: HttpClient,
        private credentialsService: CredentialsService,
    ) {
    }

    /**
     * Authenticates the user.
     * @param context The login parameters.
     * @return The user credentials.
     */
    login(context: LoginContext): Observable<Credentials> {

        if (environment.useAuthStub) {
            const credentialsReceived: Credentials = {
                accessToken:
                    'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJhdWQiOlsiKiJdLCJ1c2VyX25hbWUiOiJzaGFraXJAbXNsYy5pbiIsInNjb3BlIjpbImZvbyIsInJlYWQiLCJ3cml0ZSJdLCJ1c2VySWQiOjIxMjU2MSwiYXV0aG9yaXRpZXMiOlsiVHJhaW5lciIsIlRyYWluaW5nIFZlbmRvciIsIlVzZXIiLCJHdWVzdCJdLCJqdGkiOiIyN2M1YjRkMy1lMDcwLTQ5OGUtOWI2OS03YmMyNDAxZGE2MzkiLCJjbGllbnRfaWQiOiIxMDE1NSJ9.3LgsJoIijtR7FnHNYWDc430jyVGYGQ1AiRcVf6LKP4PbUl8uhAXrFX1mEvT2fXArTu7lS2J1ASTNxusB37Fh32H0SEPKHXTXz-xqV3ovuU-aAWDg8NzgCEbBIrSM-xYm1af1qrwfsrkfHKhrWaEP8Xpb1KUDuTbovaFuKM6DNqv-BSBwp8dIzgc2b_Pk6rMg9NGfJzCgVp7Sya2bYWI8KGXBnIkeXLqk6HGXwjZw4sG_JpD9NSpMjr4uaN2-AiZ6lD-l-EFKBPG4LBWe2RnO3RjZNKgV38Mih42gCT72OZo_5762eFiegA5F-5WGDo_FpLv941srJ0uGHXmYBrmPsQ',
                refresh_token:
                    'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJhdWQiOlsiKiJdLCJ1c2VyX25hbWUiOiJzaGFraXJAbXNsYy5pbiIsInNjb3BlIjpbImZvbyIsInJlYWQiLCJ3cml0ZSJdLCJhdGkiOiIyN2M1YjRkMy1lMDcwLTQ5OGUtOWI2OS03YmMyNDAxZGE2MzkiLCJ1c2VySWQiOjIxMjU2MSwiYXV0aG9yaXRpZXMiOlsiVHJhaW5lciIsIlRyYWluaW5nIFZlbmRvciIsIlVzZXIiLCJHdWVzdCJdLCJqdGkiOiJmOWQ3MDhjYS03OTQ5LTRhZWEtODk5ZC0zYjhhYTllYWNhNzYiLCJjbGllbnRfaWQiOiIxMDE1NSJ9.bG7JL9yjDDfIR8IjtWh1ZU6niTeYq-Q8WeB99gWml3owM0hPqBMTdJ_1XwvldT2EGFalEMmbZO0E3ONkw4rqgLNb9Qbf61TlOhFDTodftLTtJFgpdorHeOCojKdVORMhrpO-t0TmBMGvxTL6S6TLRA_r4oKSThih_l7O25uYGdL18sjSaHXMmSvrNYGRon-TWN1EIlkNpppZdaMOWkM3KfBTqIgyApb1GoqCJ-Shu3iFnW9oo2zFbgopM9B-_9Dz-lZCkT_WtrN4NnjuEt92GWmEVwiazowJLv_UcMLzsKNsNzMwZDvKzz-W9oeU67PLxXa_XRiSCK5fOsPkwcHblg',
                // userProfileImage: ''
            };
            this.credentialsService.setCredentials(credentialsReceived, context.remember);
            return null;
        }

        const loginRequest = {
            username: context.username,
            password: context.password,
        };

        const api = environment.authUrl + '/signin';
        let httpHeaders: HttpHeaders = new HttpHeaders();
        httpHeaders.append('x-origin', environment.origin);

        httpHeaders = httpHeaders.set('Authorization', 'Basic ' + btoa(environment.clientIdAndSecret));
        // this.isLoading = false;
        const authObject = this.httpClient
            .post<Credentials>(
                api,
                loginRequest,
                {
                    headers: httpHeaders,
                }
            )
            .pipe(
                tap((res) => {
                    res.username = context.username;

                    const jwtHelper = new JwtHelperService();
                    if (!res.accessToken) {
                        throw new Error('Invalid token.....');
                    }
                    const credentialsReceived: Credentials = {
                        accessToken: res.accessToken,
                        refresh_token: res.refresh_token,
                        // userProfileImage: ''
                    };
                    // following line is a must for the subsequent getCurrentUser to work
                    this.credentialsService.setCredentials(credentialsReceived, context.remember);
                })
            );

        return authObject;
    }

    /**
     * Logs out the user and clear credentials.
     * @return True if the user was logged out successfully.
     */
    logout(): Observable<boolean> {
        // Customize credentials invalidation here
        this.credentialsService.setCredentials();
        localStorage.clear();
        sessionStorage.clear();
        this.router.navigate(['/home'], {replaceUrl: true});
        return of(true);
    }
}
